import React, { useState, useRef, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Typography,
  Card,
  IconButton,
} from "@mui/material";
import LoadingButton from "components/LoadingButton";
import LockIcon from "@mui/icons-material/Lock";
import AccountCircle from "@mui/icons-material/AccountCircle";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useSnackbar } from "hooks/useSnackbar";
import FormTextInput from "components/FormTextInput";
import { onCreateSession, signIn } from "services/authService";
import { forgotPassword, resetPassword } from "services/userManagementService";
import ResetPasswordModal from "components/ResetPasswordModal";
import sessionHelper from "services/sessionHelper";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    width: "100vw",
    height: "100vh",
    background: "linear-gradient(135deg, #00082dff 0%, #2a0055ff 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  container: {
    padding: "40px",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "420px",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  header: {
    marginBottom: "32px",
    textAlign: "center",
  },
  logo: {
    display: "block",
    height: "48px",
    width: "240px",
    margin: "0 auto 16px",
    textAlign: "center",
  },
  title: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "8px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "24px",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  fieldWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldLabel: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
      "&.Mui-focused": {
        backgroundColor: "#fff",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
      fontSize: "14px",
    },
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "24px",
  },
  signInButton: {
    padding: "12px 24px",
    fontSize: "15px",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    color: "#fff",
  },
  forgotPasswordButton: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#667eea",
    textTransform: "none",
    padding: "8px 0",
    textDecoration: "underline",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#764ba2",
    },
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    margin: "20px 0",
    "& span": {
      flex: 1,
      height: "1px",
      backgroundColor: "#e0e0e0",
    },
    "& p": {
      fontSize: "13px",
      color: "#999",
      margin: 0,
    },
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "8px",
  },
  modalContent: {
    paddingTop: "16px",
    paddingBottom: "16px",
  },
  modalSubtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "20px",
  },
  modalTextField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
      fontSize: "14px",
    },
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    padding: "0 24px 24px 24px",
    justifyContent: "flex-end",
  },
}));

const SignIn = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");

  const [openResetPassword, setOpenResetPassword] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSignIn = async (credentials) => {
    try {
      setLoading(true);
      const response = await signIn(credentials);

      if (response.success) {
        if (response?.requiresPasswordReset) {
          // Show reset password modal if required
          setUserEmail(response?.email);
          setOpenResetPassword(true);
        } else {
          await onCreateSession();
          if (isMounted.current) {
            showSnackbar("Signed in successfully", "success");
            navigate("/dashboard");
          }
        }
      } else {
        if (isMounted.current)
          showSnackbar(
            "Sign in failed. Please check your credentials.",
            "error"
          );
      }
    } catch (error) {
      if (isMounted.current)
        showSnackbar(error.message || "Sign in failed", "error");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleForgotPasswordOpen = () => {
    setOpenForgotPassword(true);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
  };

  const handleForgotPasswordClose = () => {
    setOpenForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
  };

  const handleForgotPasswordSubmit = async () => {
    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError("Email is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordEmail)) {
      setForgotPasswordError("Please enter a valid email address");
      return;
    }

    try {
      setForgotPasswordLoading(true);
      setForgotPasswordError("");

      const data = await forgotPassword(forgotPasswordEmail);

      if (data?.status) {
        showSnackbar("Password reset email sent successfully", "success");
        handleForgotPasswordClose();
      } else {
        setForgotPasswordError(
          data?.message || "Failed to send reset email. Please try again."
        );
      }
    } catch (error) {
      console.error("Error:", error);
      setForgotPasswordError(
        error?.message || "An error occurred. Please try again."
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPasswordSuccess = async (params) => {
    try {
      const response = await resetPassword(params);
      if (response?.status) {
        showSnackbar("Password reset successfully", "success");

        // Update user info to remove requiresPasswordReset flag
        const userInfoStr = localStorage.getItem("userInfo");
        if (userInfoStr) {
          try {
            const userInfo = JSON.parse(userInfoStr);
            userInfo.requiresPasswordReset = false;
            localStorage.setItem("userInfo", JSON.stringify(userInfo));
          } catch (error) {
            console.error("Error updating user info:", error);
          }
        }
        await onCreateSession();

        navigate("/dashboard");
        // Close the modal - user can now access the dashboard
        setOpenResetPassword(false);
      } else {
        if (isMounted.current)
          throw new Error(response?.message || "Failed to reset password");
      }
    } catch (error) {
      if (isMounted.current) throw error;
    }
  };

  const handleLogout = () => {
    sessionHelper.onSignOut();
    window.location.reload();
  };

  return (
    <>
      <Box className={classes.root}>
        <Card className={classes.container}>
          {/* Header */}
          <Box className={classes.header}>
            <h1 className={classes.title}>Welcome Back</h1>
            <p className={classes.subtitle}>
              Sign in to your account to continue
            </p>
          </Box>

          {/* Sign In Form */}
          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={Yup.object({
              email: Yup.string()
                .email("Invalid email address")
                .required("Email is required"),
              password: Yup.string().required("Password is required"),
            })}
            onSubmit={handleSignIn}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form>
                <Box className={classes.formContainer}>
                  <Box className={classes.fieldWrapper}>
                    <label className={classes.fieldLabel}>Email Address</label>
                    <FormTextInput
                      name="email"
                      placeholder="Enter your email"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircle sx={{ color: "#667eea" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box className={classes.fieldWrapper}>
                    <label className={classes.fieldLabel}>Password</label>
                    <FormTextInput
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: "#667eea" }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              sx={{ color: "#667eea" }}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <Box className={classes.buttonGroup}>
                  <LoadingButton
                    loading={loading}
                    variant="contained"
                    type="submit"
                    fullWidth
                    className={classes.signInButton}
                    disabled={isSubmitting}
                  >
                    Sign In
                  </LoadingButton>

                  <Button
                    variant="text"
                    onClick={handleForgotPasswordOpen}
                    className={classes.forgotPasswordButton}
                    fullWidth
                  >
                    Forgot Password?
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Card>

        {/* Forgot Password Modal */}
        <Dialog
          open={openForgotPassword}
          onClose={handleForgotPasswordClose}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              borderRadius: "12px",
              minWidth: "400px",
            },
          }}
        >
          <DialogTitle className={classes.modalTitle}>
            Reset Your Password
          </DialogTitle>
          <DialogContent className={classes.modalContent} sx={{ pt: 2 }}>
            <Typography className={classes.modalSubtitle}>
              Enter your email address and we'll send you a link to reset your
              password.
            </Typography>
            <TextField
              fullWidth
              label="Email Address"
              placeholder="your.email@example.com"
              value={forgotPasswordEmail}
              onChange={(e) => {
                setForgotPasswordEmail(e.target.value);
                setForgotPasswordError("");
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleForgotPasswordSubmit();
                }
              }}
              disabled={forgotPasswordLoading}
              error={Boolean(forgotPasswordError)}
              helperText={forgotPasswordError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: "#667eea" }} />
                  </InputAdornment>
                ),
              }}
              className={classes.modalTextField}
              sx={{ marginTop: "16px" }}
            />
          </DialogContent>
          <DialogActions className={classes.modalActions}>
            <Button
              onClick={handleForgotPasswordClose}
              disabled={forgotPasswordLoading}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                minWidth: "120px",
                padding: "10px 24px",
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleForgotPasswordSubmit}
              loading={forgotPasswordLoading}
              variant="contained"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                minWidth: "140px",
                padding: "10px 24px",
              }}
            >
              Send Reset Link
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Box>

      <ResetPasswordModal
        open={openResetPassword}
        email={userEmail}
        onSuccess={handleResetPasswordSuccess}
        onLogout={handleLogout}
      />
    </>
  );
};

export default SignIn;
